import { UHttp } from "../../utils/UHttp";

const apiUrlServer = process.env.REACT_APP_URL_SERVER;

// Types pour les informations de token d'avis
export interface ReviewTokenInfo {
  id: string;
  token: string;
  bookingId: string;
  clientId: string;
  professionalId: string;
  isUsed: boolean;
  expiresAt: string;
  professional: {
    id: string;
    firstName: string;
    lastName: string;
    businessName: string;
    profession: string;
    address: string;
    city: string;
    logo?: string;
  };
  booking: {
    id: string;
    startTime: string;
    endTime: string;
    serviceId: string;
    service: {
      id: string;
      name: string;
      description?: string;
      price: number;
      duration: number;
    };
  };
  client: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

// Types pour la soumission d'un avis
export interface ReviewSubmission {
  token: string;
  clientId?: string;
  professionalId?: string;
  bookingId?: string;
  rating: number;
  comment: string;
}

// Types pour les statistiques d'avis d'un professionnel
export interface ProfessionalReviewStats {
  averageRating: number;
  count: number;
  ratingDistribution: {
    [key: number]: number;
  };
}

// Types pour un avis public
export interface PublicReview {
  id: string;
  rating: number;
  comment: string;
  response?: string;
  createdAt: string;
  updatedAt: string;
  client: {
    firstName: string;
    lastName: string;
  };
  professional?: {
    firstName: string;
    lastName: string;
    businessName?: string;
  };
  service?: {
    id: string;
    name: string;
  };
}

// Types pour les données de token d'avis retournées par le backend
export interface ReviewTokenData {
  tokenId: string;
  bookingId: string;
  clientId: string;
  companyId: string;
  booking: {
    date?: string;
    service: string;
  };
  client: {
    firstName: string;
    lastName: string;
  };
  company: {
    businessName: string;
    companyLogo?: string;
  };
}

// Types pour les réponses API
export interface ReviewTokenResponse {
  success: boolean;
  message: string;
  tokenData?: ReviewTokenData;
  tokenInfo?: ReviewTokenInfo;
  isExpired?: boolean;
  isUsed?: boolean;
}

export interface ReviewSubmitResponse {
  success: boolean;
  message: string;
  review?: {
    id: string;
    rating: number;
    comment: string;
    createdAt: string;
  };
}

export interface ProfessionalReviewsResponse {
  success: boolean;
  message: string;
  reviews: PublicReview[];
  count: number;
  reviewSystemEnabled?: boolean;
}

export interface ProfessionalStatsResponse {
  success: boolean;
  message: string;
  stats: ProfessionalReviewStats;
  reviewSystemEnabled?: boolean;
}

/**
 * Modèle pour la gestion des avis côté client
 */
export class ClientReviewModel {
  private static instance: ClientReviewModel;

  private constructor() {}

  /**
   * Retourne l'instance unique du modèle (pattern Singleton)
   */
  public static getInstance(): ClientReviewModel {
    if (!ClientReviewModel.instance) {
      ClientReviewModel.instance = new ClientReviewModel();
    }
    return ClientReviewModel.instance;
  }

  /**
   * Vérifie la validité d'un token d'avis et récupère les informations associées
   * @param token Token d'avis à vérifier
   */
  async verifyReviewToken(token: string): Promise<ReviewTokenResponse> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/public-api/review/token/${token}`,
        UHttp.publicApiHeaders()
      );
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error("Erreur lors de la vérification du token d'avis:", error);
      return {
        success: false,
        message: error.message || 'Erreur lors de la vérification du token',
        isExpired: error.response?.data?.isExpired || false,
        isUsed: error.response?.data?.isUsed || false
      };
    }
  }

  /**
   * Soumet un avis client
   * @param reviewData Données de l'avis à soumettre
   */
  async submitReview(reviewData: ReviewSubmission): Promise<ReviewSubmitResponse> {
    try {
      const response = await UHttp.post(
        `${apiUrlServer}/public-api/review`,
        reviewData,
        UHttp.publicApiHeaders()
      );
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error("Erreur lors de la soumission de l'avis:", error);
      return {
        success: false,
        message: error.message || 'Erreur lors de la soumission de l\'avis'
      };
    }
  }

  /**
   * Récupère les avis publics d'une entreprise
   * @param companyId ID de l'entreprise (peut aussi être passé comme professionalId pour compatibilité)
   */
  async getProfessionalReviews(companyId: string): Promise<ProfessionalReviewsResponse> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/public-api/review/company/${companyId}`,
        UHttp.publicApiHeaders()
      );
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error("Erreur lors de la récupération des avis de l'entreprise:", error);
      return {
        success: false,
        message: error.message || 'Erreur lors de la récupération des avis',
        reviews: [],
        count: 0
      };
    }
  }

  /**
   * Récupère les statistiques d'avis d'une entreprise
   * @param companyId ID de l'entreprise (peut aussi être passé comme professionalId pour compatibilité)
   */
  async getProfessionalStats(companyId: string): Promise<ProfessionalStatsResponse> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/public-api/review/stats/${companyId}`,
        UHttp.publicApiHeaders()
      );
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error("Erreur lors de la récupération des statistiques d'avis:", error);
      return {
        success: false,
        message: error.message || 'Erreur lors de la récupération des statistiques',
        stats: {
          averageRating: 0,
          count: 0,
          ratingDistribution: {}
        }
      };
    }
  }
}

export default ClientReviewModel.getInstance();
